// /calendar/cycle — the independent cycle view, as a real route.
//
// This was in-page state in the first cut, which contradicted DESIGN §2: the
// spec lists the cycle view at `/calendar/cycle` and explicitly marks the
// other three extra screens 「（sheet）」. A state toggle is not a route, and
// the difference is user-visible — the screen could not be linked, the back
// button did nothing, and a refresh lost it.
//
// **W58.5 —— 這一页右上角多了一顆主行動按鈕。**
//
// 使用者要求：「**比照日曆頁右上角「+ 新增」按鈕的對齊位置與設計模式，在週期頁面
// 頂部…加入顯眼的「經期來了」（若在經期中則為「標記結束」）主按鈕**」。
//
// **為什麼放在 `PageHero` 的 `trailing` 槽。** 那正是日曆頁「+ 新增」的位置，
// 而 `PageHero` 是全站 21 個頁面**同一個元件** —— 所以標題的高度、字級、
// 左右邊距與按鈕位置**不需要對齊**，它們本來就是同一份。
//
// 標籤是「經期來了」還是「標記結束」，取決於**今天是不是在實際經期裡** ——
// 而「實際經期」是**事實**（有一筆記錄的經期事件覆蓋今天），不是推算。

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { PageHero } from "@/components/primitives/PageHero";
import { ErrorState } from "@/components/primitives/ErrorState";
import { CycleView } from "@/components/calendar/CycleView";
import { CycleFlower } from "@/components/calendar/CycleFlower";
import {
  isActualPeriodDay,
  periodEventOn,
  todayIso,
} from "@/components/calendar/cyclePeriod";
import { getCycle, updateEvent } from "@/lib/api/calendar";
import { queryKeys } from "@/lib/queries/keys";

export function CalendarCyclePage() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ["calendar-cycle"], queryFn: getCycle });

  const cycle = query.data ?? null;
  const today = todayIso();

  // **「今天在實際經期裡」是事實** —— 從**記錄**判定，不是從推算判定。
  // （推算的 `predicted_start` 是**下一輪**的起點，用它判斷會在一個沒有
  // 任何經期記錄的配對上憑空造出經期。見 ADR-0211。）
  const inPeriod = cycle ? isActualPeriodDay(cycle.history, today) : false;
  const periodEvent = cycle ? periodEventOn(cycle.history, today) : null;

  const endMutation = useMutation({
    mutationFn: (eventId: string) => updateEvent(eventId, { end_date: today }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["calendar-cycle"] });
      await qc.invalidateQueries({ queryKey: ["calendar-month"] });
      await qc.invalidateQueries({ queryKey: ["calendar-day"] });
      // W51 D-1 — 任何寫入都要讓首頁失效（`useDashboard` 有 30 秒 staleTime）。
      void qc.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });

  return (
    // W58.5 — **外層容器與 `/calendar` 對齊。**
    //
    // 這一頁原本帶著自己的 `pb-32`，而 **`MobileShell` 已經把 `<main>` 墊成
    // `pb-32`**（`components/AppShell/MobileShell.tsx`）—— 兩者疊起來是
    // **256px** 的底部空白。而且同一個模組的 `/calendar` **沒有**自己那一層，
    // 於是模組內兩個頁面的底部留白不一致。
    //
    // 「重構週期頁的外層容器，確保與其他頁面 100% 視覺對齊」就是這件事：
    // 底部留白由**外層**負責，頁面自己不再加第二層。
    <div data-testid="calendar-cycle-page" className="flex flex-col">
      {/* Title lives in the PageHero; CycleView's own header is suppressed
          here via `showHeader={false}` so the route wrapper does not render
          it twice. In the first cut the screen was page state and the hero was
          absent, which is why the duplication only became visible now. */}
      <PageHero
        eyebrow="協作記錄"
        title="週期"
        // W58.5 — **主行動按鈕，與日曆頁的「+ 新增」同一個位置、同一個形狀。**
        //
        // `cta-pill` 是 `.cta-pill` 這個共用類（globals.css）—— 它帶著
        // 那道 press 反饋與那個墨色底。日曆頁的「+ 新增」用的就是它，
        // 所以兩顆按鈕**量出來的尺寸與位置是一樣的**。
        trailing={
          inPeriod && periodEvent ? (
            <button
              type="button"
              onClick={() => endMutation.mutate(periodEvent.event_id)}
              disabled={endMutation.isPending}
              aria-label="標記經期結束"
              data-testid="calendar-cycle-end-period"
              className="press cta-pill hover:opacity-90 transition-opacity disabled:opacity-60"
            >
              <CycleFlower size={18} aria-hidden />
              標記結束
            </button>
          ) : (
            <button
              type="button"
              onClick={() =>
                nav(
                  `/calendar/events/new?date=${encodeURIComponent(today)}&category=cycle`,
                )
              }
              aria-label="經期來了"
              data-testid="calendar-cycle-start-period"
              className="press cta-pill hover:opacity-90 transition-opacity"
            >
              <CycleFlower size={18} aria-hidden />
              經期來了
            </button>
          )
        }
      />

      {/* W48.0 / ADR-0146 — same change as `/calendar`: mobile full width,
          `md:` keeps the centred 390 column. Leaving this one capped would give
          the calendar module two different left edges for the same 430px phone. */}
      <div className="mx-auto w-full px-s-4 py-s-5 md:max-w-[390px]">
        {query.isError ? (
          <ErrorState
            title="無法載入週期紀錄"
            description="請稍候再試一次"
            onRetry={() => void query.refetch()}
          />
        ) : (
          <CycleView
            cycle={cycle}
            loading={query.isLoading}
            // W58.1 — 「補記一次」開的是一個**已經是週期類型**的表單，
            // 結束日也已經依平均經期長度自動展開。之前這裡是
            // `/calendar?new=1`，而月曆頁從來沒有讀過 `new` 這個參數 ——
            // 那顆按鈕其實什麼都沒做。
            onRecord={() =>
              nav(
                `/calendar/events/new?date=${encodeURIComponent(today)}&category=cycle`,
              )
            }
            showHeader={false}
          />
        )}
      </div>
    </div>
  );
}
